import { CHIP_ICONS } from '../chipIcons'
import SharpestWeekCard from './SharpestWeekCard'
import { CHIP_TILE_SHORT_NAMES } from './records'
import type { BestGameweekRecord, ChipTile } from './records'
import './ProfileRecords.css'

export interface ProfileRecordsProps {
  bestGameweek: BestGameweekRecord | null
  /** Every gameweek the sharpest-week row's arrows should step through. Absent on your own
   *  profile, where the modal already walks your season on its own. */
  sharpestWeekWalk?: number[]
  chips: ChipTile[]
  /** Opens the gameweek results modal. `walk` is every gameweek its arrows should step through.
   *  Absent leaves every row inert. */
  onOpenGameweek?: (gameweek: number, walk?: number[]) => void
}

/**
 * The two records under the Intuition Rating card: the season's sharpest week, and
 * what each chip was spent on. Both are doors into the gameweek results modal, on your own
 * profile and on anyone else's.
 *
 * Sharpest week is measured on **base points** — chip-free 5/3/1 with the Banker doubled — so it
 * can disagree with the Double Up tile directly beneath it. One says how well the football was
 * read, the other when the chip was spent; if they were the same number the row would be dead.
 */
function ProfileRecords({
  bestGameweek,
  sharpestWeekWalk,
  chips,
  onOpenGameweek,
}: ProfileRecordsProps) {
  // Nothing to show before GW1 settles and before a ledger arrives — the section is absent, not
  // an empty state full of dashes.
  if (bestGameweek === null && chips.length === 0) return null

  return (
    <section className="profile-records">
      <SharpestWeekCard
        record={bestGameweek}
        walk={sharpestWeekWalk}
        onOpenGameweek={onOpenGameweek}
      />

      {chips.length > 0 && (
        <div className="profile-records__row">
          <p className="profile-records__label label-eyebrow">Chips</p>
          <div className="profile-records__chips">
            {chips.map((chip) => (
              <ChipTileCard key={chip.id} chip={chip} onOpenGameweek={onOpenGameweek} />
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

function ChipTileCard({
  chip,
  onOpenGameweek,
}: {
  chip: ChipTile
  onOpenGameweek?: (gameweek: number, walk?: number[]) => void
}) {
  const Icon = CHIP_ICONS[chip.id]
  // Only a played chip is a record with a week behind it. Live has no result yet and unplayed
  // has no week, so neither is a door.
  const target = chip.state === 'played' ? (chip.gameweek ?? undefined) : undefined
  const isTappable = target !== undefined && onOpenGameweek !== undefined

  // Gated on settlement, exactly as `useChips` gates its frozen labels: the server writes scored
  // rows mid-afternoon, so a number before then is a half-played week presented as a record.
  // Activated has no result yet either, so it names itself instead.
  const value =
    chip.state === 'live'
      ? 'Activated'
      : chip.state === 'played' && chip.points !== null
        ? `${chip.points} pts`
        : ''

  // The visual tile is fragments in corners and stacked rows, and it renders the name twice so
  // CSS can pick a shorter one on narrow screens. Read straight, that is a run-on of pieces — so
  // the button carries one written label and every visual child is hidden from the a11y tree.
  const label = [
    chip.name,
    `${chip.used} of ${chip.seasonCap} used`,
    chip.state === 'live' && 'activated this gameweek',
    chip.state === 'played' && `gameweek ${chip.gameweek}`,
    chip.state === 'played' && chip.points !== null && `${chip.points} points`,
    chip.state === 'unplayed' && 'not played',
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <button
      type="button"
      className={`profile-records__chip profile-records__chip--${chip.state}`}
      disabled={!isTappable}
      aria-label={label}
      // The face leads with the best week; the arrows in the modal are what reach the others,
      // so a two-charge chip needs no list screen of its own.
      onClick={isTappable ? () => onOpenGameweek(target, chip.gameweeks) : undefined}
    >
      {/* Metadata lives in the corners so nothing competes with the icon for the tile's optical
          centre. Left is the charge counter in every state — it says what the pips said, works
          for any number of charges, and doesn't invent a second idiom. */}
      <span
        className="profile-records__chip-corner profile-records__chip-corner--left"
        aria-hidden="true"
      >
        {chip.used} of {chip.seasonCap} used
      </span>
      {/* Right is the gameweek, and only where *when it was burned* is the point. An activated
          chip means now, and the page header already says which gameweek that is. */}
      {chip.state === 'played' && (
        <span
          className="profile-records__chip-corner profile-records__chip-corner--right"
          aria-hidden="true"
        >
          GW{chip.gameweek}
        </span>
      )}

      {/* Fixed 56px in every state: played and unplayed put a 36px icon in it, activated fills
          the same box with the disc. If the slot could resize, every tile in the row shifts. */}
      <span className="profile-records__chip-slot">
        <Icon
          className="profile-records__chip-icon"
          strokeWidth={1.6}
          aria-hidden="true"
        />
      </span>

      {/* Both rendered, one shown per width — CSS cannot swap text, and the shorter face name is
          the reference's documented fallback for the narrowest phones. */}
      <span
        className="profile-records__chip-name profile-records__chip-name--full"
        aria-hidden="true"
      >
        {chip.name}
      </span>
      <span
        className="profile-records__chip-name profile-records__chip-name--short"
        aria-hidden="true"
      >
        {CHIP_TILE_SHORT_NAMES[chip.id]}
      </span>
      {/* Always rendered, even empty: the row keeps its height so all three tiles land their
          names and numbers on the same baselines instead of each centring its own content. */}
      <span className="profile-records__chip-value" aria-hidden="true">
        {value}
      </span>
    </button>
  )
}

export default ProfileRecords
